import { CheckResult } from '../types';
import { inspectActiveVideoTrack } from './webcam-device-utils';

function buildMetadata(
  inspection: Awaited<ReturnType<typeof inspectActiveVideoTrack>>,
) {
  return {
    activeDeviceLabel: inspection.activeDeviceLabel,
    activeDeviceId: inspection.activeDeviceId,
    activeDeviceIsVirtual: inspection.activeDeviceIsVirtual,
    installedVirtualDevices: inspection.installedVirtualDevices,
    detectionSource: inspection.detectionSource,
    capabilityReason: inspection.capabilityReason,
  };
}

export async function webcamCheck(): Promise<CheckResult> {
  const timestamp = new Date().toISOString();
  let probeStream: MediaStream | null = null;

  try {
    if (!navigator.mediaDevices?.getUserMedia) {
      return {
        checkId: 'webcam_virtual',
        checkName: 'Webcam Virtual',
        status: 'warning',
        details:
          'Não foi possível validar a câmera neste navegador. Libere a permissão e tente novamente.',
        timestamp,
      };
    }

    probeStream = await navigator.mediaDevices.getUserMedia({
      video: true,
    });

    const videoTrack = probeStream.getVideoTracks()[0];
    if (!videoTrack) {
      return {
        checkId: 'webcam_virtual',
        checkName: 'Webcam Virtual',
        status: 'failed',
        details:
          'Nenhuma webcam foi detectada. Conecte uma webcam física para continuar.',
        timestamp,
        metadata: {
          activeDeviceIsVirtual: undefined,
          installedVirtualDevices: [],
          detectionSource: 'unknown',
        },
      };
    }

    const devices = navigator.mediaDevices?.enumerateDevices
      ? await navigator.mediaDevices.enumerateDevices()
      : [];
    const inspection = await inspectActiveVideoTrack(videoTrack, devices);

    if (inspection.activeDeviceIsVirtual === true) {
      return {
        checkId: 'webcam_virtual',
        checkName: 'Webcam Virtual',
        status: 'failed',
        details: `Detectamos câmera virtual em uso: "${
          inspection.activeDeviceLabel || 'Câmera virtual'
        }". Selecione uma webcam física para continuar.`,
        timestamp,
        metadata: buildMetadata(inspection),
      };
    }

    if (inspection.activeDeviceIsVirtual === false) {
      return {
        checkId: 'webcam_virtual',
        checkName: 'Webcam Virtual',
        status: 'passed',
        details: `Webcam ativa validada: ${
          inspection.activeDeviceLabel || 'Webcam física'
        }.`,
        timestamp,
        metadata: buildMetadata(inspection),
      };
    }

    return {
      checkId: 'webcam_virtual',
      checkName: 'Webcam Virtual',
      status: 'warning',
      details:
        'Não foi possível confirmar se a câmera ativa é física. Revise o dispositivo selecionado e valide novamente.',
      timestamp,
      metadata: buildMetadata(inspection),
    };
  } catch (error) {
    const nextError = error as DOMException;
    if (
      nextError?.name === 'NotAllowedError' ||
      nextError?.name === 'PermissionDeniedError'
    ) {
      return {
        checkId: 'webcam_virtual',
        checkName: 'Webcam Virtual',
        status: 'warning',
        details:
          'Permissão de câmera negada. Libere o acesso no navegador e tente novamente.',
        timestamp,
      };
    }

    return {
      checkId: 'webcam_virtual',
      checkName: 'Webcam Virtual',
      status: 'warning',
      details:
        'Erro ao validar a câmera em uso. Selecione uma webcam física e tente novamente.',
      timestamp,
    };
  } finally {
    probeStream?.getTracks().forEach(track => track.stop());
  }
}
